import type { ReactNode } from 'react'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import styles from '../../styles/components/assets/AssetsWorkspaceTabs.module.scss'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

export type AssetsWorkspaceTab = 'information-systems' | 'inventory' | 'data-map' | 'risk-compliance'

export interface AssetsWorkspaceProps {
  activeTab: AssetsWorkspaceTab
  onTabChange: (tab: AssetsWorkspaceTab) => void
  children: ReactNode
}

const TAB_KEYS: AssetsWorkspaceTab[] = [
  'information-systems',
  'inventory',
  'data-map',
  'risk-compliance',
]

export function AssetsWorkspace({ activeTab, onTabChange, children }: AssetsWorkspaceProps) {
  const { t } = useI18n()
  const enterprise = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1

  const tabs = TAB_KEYS.map((key) => (
    <button
      key={key}
      type="button"
      className={
        enterprise
          ? `${entStyles.workspaceNavTab} ${activeTab === key ? entStyles.workspaceNavTabActive : ''}`.trim()
          : `${styles.tab} ${activeTab === key ? styles.tabActive : ''}`.trim()
      }
      onClick={() => onTabChange(key)}
      aria-selected={activeTab === key}
      role="tab"
    >
      <span className={enterprise ? undefined : styles.tabLabel}>
        {t(`assets.tabs.${key}`, key)}
      </span>
    </button>
  ))

  return (
    <>
      {enterprise ? (
        <nav className={entStyles.workspaceNavPanel} aria-label={t('assets.tabs.ariaLabel', 'Assets workspace sections')}>
          <div className={entStyles.workspaceNavTrack}>{tabs}</div>
        </nav>
      ) : (
        <nav className={styles.root} aria-label={t('assets.tabs.ariaLabel', 'Assets workspace sections')}>
          <div className={styles.track}>{tabs}</div>
        </nav>
      )}
      <div role="tabpanel">{children}</div>
    </>
  )
}
